import {
  Button,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from '@ibsys/kit';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/shared/ui';

type ButtonProps = React.ComponentProps<typeof Button>;

type Props = {
  className?: string;
  onConfirm: () => void | Promise<unknown>;
  isLoadingConfirm?: boolean;
  title: React.ReactNode;
  description?: React.ReactNode;
  trigger?: ButtonProps;
};

/**
 * Кнопка деструктивного действия с диалогом подтверждения.
 * По умолчанию — иконка корзины; доступное имя берётся из перевода,
 * если триггер не задаёт собственный текст, свою иконку или aria-label.
 */
export const ConfirmationButton: FC<Props> = (props) => {
  const { className, onConfirm, title, description, trigger, isLoadingConfirm } = props;

  const [open, setOpen] = useState(false);

  const { t } = useTranslation('common');

  const handleConfirm = async () => {
    await onConfirm();
    setOpen(false);
  };

  // Кнопка без подписи остаётся иконочной — ей нужно доступное имя для клавиатуры и чтения с экрана.
  // Имя «Удалить» относится только к дефолтной иконке корзины: со своей иконкой действие уже другое
  // (восстановление, очистка), и имя ей задаёт вызывающий — подписью или `aria-label` в trigger.
  const iconOnlyLabel = trigger?.children || trigger?.icon ? undefined : t('delete');

  return (
    <DialogRoot onOpenChange={setOpen} open={open}>
      <DialogTrigger asChild>
        <Button
          icon={<Icon type={'trash'} />}
          variant={'ghost'}
          color={'destructive'}
          aria-label={iconOnlyLabel}
          {...trigger}
          className={className}
        />
      </DialogTrigger>
      <DialogContent className={'w-max'}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {description && <DialogDescription>{description}</DialogDescription>}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant={'ghost'}>{t('cancel')}</Button>
          </DialogClose>
          <Button color={'destructive'} variant={'outline'} onClick={handleConfirm} isLoading={isLoadingConfirm}>
            {t('confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
